import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Globe, 
  Facebook, 
  Instagram,
  Loader2
} from 'lucide-react';
import apiService from '../services/api';
import { SettingsObject } from '../types';
import toast from 'react-hot-toast';

interface SettingsFormData {
  site_name: string;
  site_description: string;
  hero_title: string;
  hero_subtitle: string;
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  opening_hours: string;
  social_facebook: string;
  social_instagram: string;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsFormData>({
    site_name: '',
    site_description: '',
    hero_title: '',
    hero_subtitle: '',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    opening_hours: '',
    social_facebook: '',
    social_instagram: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<SettingsFormData>({} as SettingsFormData);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsData = await apiService.getSettings();
      
      const formData: SettingsFormData = {
        site_name: settingsData.site_name || '',
        site_description: settingsData.site_description || '',
        hero_title: settingsData.hero_title || '',
        hero_subtitle: settingsData.hero_subtitle || '',
        contact_phone: settingsData.contact_phone || '',
        contact_email: settingsData.contact_email || '',
        contact_address: settingsData.contact_address || '',
        opening_hours: settingsData.opening_hours || '',
        social_facebook: settingsData.social_facebook || '',
        social_instagram: settingsData.social_instagram || '',
      };
      
      setSettings(formData);
      setOriginalSettings(formData);
    } catch (error: any) {
      toast.error('Failed to load settings');
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SettingsFormData, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update only changed settings
      const updates: Array<{ key: string; value: string }> = [];
      
      (Object.keys(settings) as Array<keyof SettingsFormData>).forEach(key => {
        if (settings[key] !== originalSettings[key]) {
          updates.push({ key, value: settings[key] });
        }
      });

      if (updates.length === 0) {
        toast.success('No changes to save');
        return;
      }

      // Save each changed setting
      for (const { key, value } of updates) {
        await apiService.updateSetting(key, value);
      }

      setOriginalSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error: any) {
      toast.error('Failed to save settings');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return (Object.keys(settings) as Array<keyof SettingsFormData>).some(
      key => settings[key] !== originalSettings[key]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your website configuration and contact information
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Site Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white shadow rounded-lg p-6"
        >
          <div className="flex items-center mb-4">
            <Globe className="w-5 h-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Site Information</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Name
              </label>
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => handleInputChange('site_name', e.target.value)}
                placeholder="Eden Spot Garden"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Description
              </label>
              <textarea
                value={settings.site_description}
                onChange={(e) => handleInputChange('site_description', e.target.value)}
                rows={3}
                placeholder="Your peaceful getaway just off Thika Road"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hero Title
              </label>
              <input
                type="text"
                value={settings.hero_title}
                onChange={(e) => handleInputChange('hero_title', e.target.value)}
                placeholder="Eden Spot Garden"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hero Subtitle
              </label>
              <input
                type="text"
                value={settings.hero_subtitle}
                onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                placeholder="Where memories bloom in every season"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white shadow rounded-lg p-6"
        >
          <div className="flex items-center mb-4">
            <Phone className="w-5 h-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Contact Information</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="text"
                value={settings.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                placeholder="+254 722 140253"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={settings.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                placeholder="edenspotgarden@gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Physical Address
              </label>
              <textarea
                value={settings.contact_address}
                onChange={(e) => handleInputChange('contact_address', e.target.value)}
                rows={2}
                placeholder="Ruiru Kimbo, Behind Vishnu Industrial Park"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Opening Hours
              </label>
              <input
                type="text"
                value={settings.opening_hours}
                onChange={(e) => handleInputChange('opening_hours', e.target.value)}
                placeholder="Mon-Sun: 8AM - 8PM"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </motion.div>

        {/* Social Media */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white shadow rounded-lg p-6 lg:col-span-2"
        >
          <div className="flex items-center mb-4">
            <Facebook className="w-5 h-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Social Media</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Facebook className="w-4 h-4 inline mr-1" />
                Facebook Page URL
              </label>
              <input
                type="url"
                value={settings.social_facebook}
                onChange={(e) => handleInputChange('social_facebook', e.target.value)}
                placeholder="https://facebook.com/edenspotgarden"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Instagram className="w-4 h-4 inline mr-1" />
                Instagram Profile URL
              </label>
              <input
                type="url"
                value={settings.social_instagram}
                onChange={(e) => handleInputChange('social_instagram', e.target.value)}
                placeholder="https://instagram.com/edenspotgarden"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Save Button (Mobile) */}
      <div className="block lg:hidden">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving Changes...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Changes indicator */}
      {hasChanges() && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 rounded-md p-4"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                You have unsaved changes. Don't forget to save your settings.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Settings;